import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tag, Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * SalesBannerTab — manual CMS for shops that don't have an auto-pull endpoint
 * (Trulieve, etc.). Owners create / edit / pause / delete their own banners
 * which surface on the consumer-facing dispensary detail page next to
 * auto-pulled banners.
 */
export const SalesBannerTab = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id...} = edit

  const refresh = () => {
    setLoading(true);
    api
      .get("/dispensary/sales-banners")
      .then((r) => setBanners(r.data?.results || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const onToggleActive = async (b) => {
    try {
      await api.put(`/dispensary/sales-banners/${b.id}`, { active: !b.active });
      toast.success(b.active ? "Banner paused" : "Banner activated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Delete banner "${b.headline}"?`)) return;
    try {
      await api.delete(`/dispensary/sales-banners/${b.id}`);
      toast.success("Banner deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="eyebrow mb-1">Sales banners</div>
          <h2 className="font-serif text-2xl">Your live promotions</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Push a sale or featured drop straight to your shop&apos;s detail
            page on Bud Link. Patients see it the moment they land on your
            store.
          </p>
        </div>
        <Button
          data-testid="banner-add"
          onClick={() => setEditing({})}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-5"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New banner
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
      ) : banners.length === 0 ? (
        <div
          data-testid="banner-empty"
          className="border border-dashed border-border rounded-2xl p-12 text-center"
        >
          <Sparkles className="w-6 h-6 mx-auto mb-3 text-[#d4a268]" />
          <div className="font-serif text-xl mb-2">No banners yet</div>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Create your first promo — &quot;20% off all flower this weekend&quot;,
            &quot;BOGO pre-rolls&quot;, &quot;New drop: Cookies&quot;. Stays
            live until you pause it or its end date passes.
          </p>
          <Button
            data-testid="banner-add-empty"
            onClick={() => setEditing({})}
            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-5"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create your first banner
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((b) => (
            <BannerRow
              key={b.id}
              banner={b}
              onEdit={() => setEditing(b)}
              onToggle={() => onToggleActive(b)}
              onDelete={() => onDelete(b)}
            />
          ))}
        </div>
      )}

      <BannerEditor
        open={editing !== null}
        initial={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />
    </div>
  );
};

const BannerRow = ({ banner, onEdit, onToggle, onDelete }) => {
  const ends = banner.ends_at ? new Date(banner.ends_at) : null;
  const isExpired = ends && ends.getTime() < Date.now();
  return (
    <div
      data-testid={`banner-row-${banner.id}`}
      className="border border-border bg-card rounded-2xl p-5 flex items-start gap-4 flex-wrap"
    >
      <div className="flex items-start gap-3 flex-1 min-w-[200px]">
        <Tag className="w-4 h-4 text-[#d4a268] mt-1 shrink-0" />
        <div className="min-w-0">
          <div className="font-serif text-lg leading-tight">
            {banner.headline}
          </div>
          {banner.description && (
            <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {banner.description}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px] uppercase tracking-wider">
            {banner.active && !isExpired ? (
              <span className="pill bg-emerald-50 text-emerald-800 border-emerald-200">
                Live
              </span>
            ) : isExpired ? (
              <span className="pill bg-amber-50 text-amber-800 border-amber-200">
                Expired
              </span>
            ) : (
              <span className="pill bg-secondary text-foreground/60">
                Paused
              </span>
            )}
            {ends && (
              <span className="text-foreground/50 normal-case tracking-normal">
                Ends{" "}
                {ends.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <div className="flex items-center gap-2 mr-2">
          <Switch
            data-testid={`banner-toggle-${banner.id}`}
            checked={banner.active}
            onCheckedChange={onToggle}
          />
          <span className="text-xs text-muted-foreground">Active</span>
        </div>
        <Button
          data-testid={`banner-edit-${banner.id}`}
          variant="outline"
          size="sm"
          onClick={onEdit}
          className="rounded-full h-9"
        >
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
        </Button>
        <Button
          data-testid={`banner-delete-${banner.id}`}
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="rounded-full h-9 text-destructive hover:text-destructive"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

const toDateInput = (iso) => {
  if (!iso) return "";
  // Accept both date-only and full ISO datetime
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
};

const BannerEditor = ({ open, initial, onClose, onSaved }) => {
  const isEdit = initial && initial.id;
  const [form, setForm] = useState({
    headline: "",
    description: "",
    image_url: "",
    ends_at: "",
    active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      headline: initial?.headline || "",
      description: initial?.description || "",
      image_url: initial?.image_url || "",
      ends_at: toDateInput(initial?.ends_at),
      active: initial?.active ?? true,
    });
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.headline.trim()) {
      toast.error("Headline is required");
      return;
    }
    setSaving(true);
    const payload = {
      headline: form.headline.trim(),
      description: form.description.trim(),
      image_url: form.image_url.trim(),
      ends_at: form.ends_at
        ? new Date(`${form.ends_at}T23:59:59`).toISOString()
        : null,
      active: form.active,
    };
    try {
      if (isEdit) {
        await api.put(`/dispensary/sales-banners/${initial.id}`, payload);
        toast.success("Banner updated");
      } else {
        await api.post("/dispensary/sales-banners", payload);
        toast.success("Banner created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="banner-editor-dialog"
        className="max-w-lg"
      >
        <DialogTitle className="font-serif text-2xl">
          {isEdit ? "Edit banner" : "New sales banner"}
        </DialogTitle>
        <DialogDescription>
          Promo or featured drop pinned to your shop&apos;s page on Bud Link.
        </DialogDescription>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="banner-headline" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Headline *
            </Label>
            <Input
              id="banner-headline"
              data-testid="banner-headline-input"
              value={form.headline}
              maxLength={140}
              onChange={(e) =>
                setForm((f) => ({ ...f, headline: e.target.value }))
              }
              placeholder="e.g. 20% off all flower this weekend"
              className="h-11 mt-1.5"
              required
            />
          </div>
          <div>
            <Label htmlFor="banner-desc" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Description (optional)
            </Label>
            <textarea
              id="banner-desc"
              data-testid="banner-desc-input"
              value={form.description}
              maxLength={600}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              rows={3}
              placeholder="Mix-and-match eighths. While supplies last."
              className="w-full mt-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <Label htmlFor="banner-image" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Image URL (optional)
            </Label>
            <Input
              id="banner-image"
              data-testid="banner-image-input"
              value={form.image_url}
              onChange={(e) =>
                setForm((f) => ({ ...f, image_url: e.target.value }))
              }
              placeholder="https://..."
              className="h-11 mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="banner-ends" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              End date (optional)
            </Label>
            <Input
              id="banner-ends"
              data-testid="banner-ends-input"
              type="date"
              value={form.ends_at}
              onChange={(e) =>
                setForm((f) => ({ ...f, ends_at: e.target.value }))
              }
              className="h-11 mt-1.5"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Leave blank for an open-ended promo. You can pause anytime.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              data-testid="banner-active-input"
              checked={form.active}
              onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
            />
            <Label className="text-sm">
              Live on your shop page right now
            </Label>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-full h-10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="banner-save"
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Publish banner"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
